import React, {Component} from "react"
import { CDN_PUB_URL } from "../../../../constants"
import { MathKatex2 } from "../../../../components/MathKatex"
import { HighlighterACT } from "../../../../components/Highlighter"
import { IAnsweredQuestion } from "../../../../services/practice-test-scoring/practice-test-summary"
import { clearHighlights, findAndHighlightText, scrollToAnchor, prepareContentWithCDNImages } from "../../../../services/utils/html-util"
import AnswersGroup from "./AnswersGroup"
import ComputerTestACT from "./ComputerTestACT"
import "./QuestionACT.css"

interface IACTQuestionProps {
    baseUrlCDN?: string
    question: IAnsweredQuestion
    highlighterEnabled: boolean
    onAnswer: (question, userAnswer: string) => void
    onBookmark: (bookmarked) => void
    onHighlight: (highlight) => void
    onBack: () => void
    onNext: () => void
    disableBack: boolean
    disableNext: boolean
    totalQuestionsCount?: number
}

interface IACTQuestinState {
}

export default class QuestionACT extends Component<IACTQuestionProps, IACTQuestinState> {
    constructor(props) {
        super(props)
    }

    prepareChoice = (choice?: string) => {
        if (!choice) return ""
        if (choice.includes("$CDN")) {
            let url = choice.replace("$CDN", this.props.baseUrlCDN || CDN_PUB_URL).trim()
            return `<img src=${url} class="question-option" alt="option"/>`
        }
        if (choice.toLowerCase() === "no change") {
            return `<b>${choice}</b>`
        }
        return choice
    }

    handleBookmark = () => {
        let {question, onBookmark} = this.props
        onBookmark(question)
    }

    handleAnswer = (answer) => {
        let {question, onAnswer} = this.props
        onAnswer(question, answer)
    }

    handleEliminate = (choice, eliminated) => {
        let {question} = this.props
        if (!question.eliminatedInfo) {
            question.eliminatedInfo = {}
        }
        question.eliminatedInfo[choice] = eliminated
    }

    handleReverseMask = (choice, reverseMasked) => {
        let {question} = this.props
        if (!question.reverseMaskedInfo) {
            question.reverseMaskedInfo = {}
        }
        question.reverseMaskedInfo[choice] = reverseMasked
    }

    handleHighlightLeft = (highlightedHTML) => {
        let {question, onHighlight} = this.props
        if (highlightedHTML !== "") {
            question.highlightedHTMLLeft = highlightedHTML
        }
        else {
            question.highlightedHTMLLeft = undefined
        }
        onHighlight(question)
    }

    handleHighlightRight = (highlightedHTML) => {
        let {question, onHighlight} = this.props
        if (highlightedHTML !== "") {
            question.highlightedHTMLRight = highlightedHTML
        }
        else {
            question.highlightedHTMLRight = undefined
        }
        onHighlight(question)
    }

    render() {
        let {question} = this.props
        if (!question) return null

        let { eliminatedInfo, reverseMaskedInfo, questionNumber, 
            mcLetters, questionText, questionBox, questionQualifier,
            questionBoxHighlight, questionBoxAnchor,
            choiceA, choiceB, choiceC, choiceD, choiceE,
            userSubmittedAnswer} = question
        let {highlighterEnabled, onBack, onNext, disableBack, disableNext} = this.props

        if (!ComputerTestACT.TOOLS_MAP.answerEliminator.selected) eliminatedInfo = {}
        if (!ComputerTestACT.TOOLS_MAP.answerMasking.selected) reverseMaskedInfo = {}

        // If there is no specific class information specifed then make it bold!
        if (questionText && !questionText.includes("class=")) {
            questionText = `<b>${questionText}</b>`
        }

        let questionBoxContent
        if (questionBox) {
            questionBox = prepareContentWithCDNImages(this.props.baseUrlCDN || CDN_PUB_URL, questionBox, "question-box-image")
            questionBoxContent = question.highlightedHTMLLeft ? question.highlightedHTMLLeft: questionBox
            setTimeout(() => {
                let elementId = "act-passage"
                clearHighlights(elementId)
                findAndHighlightText(elementId, questionBoxHighlight)
                scrollToAnchor(elementId, questionBoxAnchor)
            }, 100)
        }

        let preparedQuestionQualifier = prepareContentWithCDNImages(this.props.baseUrlCDN || CDN_PUB_URL, questionQualifier, "question-qualifier-image")
        let choices = [choiceA, choiceB, choiceC, choiceD, choiceE]
        let options = mcLetters.map((mcLetter, idx) => {
            // Use the supplied ACT letters, including E/K for five-option math questions.
            return {
                value: mcLetter,
                label: this.prepareChoice(choices[idx])
            }
        })

        preparedQuestionQualifier = question.highlightedHTMLRight ? question.highlightedHTMLRight: preparedQuestionQualifier

        return (
            <div className="act-main-container">
                <div className="act-question">
                    <QuestionInfo question={question} totalQuestionsCount={this.props.totalQuestionsCount} onBookmark={this.handleBookmark} />
                    <div className="act-question-content">
                        {questionBox ?
                            <>
                                <div id="act-passage" className={`act-question-passage ${highlighterEnabled ? "": ""}`}>
                                    <div className="act-qna-wrapper">
                                        <div className="eact-title-card">
                                            <MathKatex2 mixedContent={questionText ?? ""} />
                                        </div>
                                        <HighlighterACT enabled={highlighterEnabled} onHighlight={this.handleHighlightLeft}>
                                            <MathKatex2 mixedContent={questionBoxContent} />
                                        </HighlighterACT>
                                    </div>
                                </div>
                                <div className="act-question-with-answers">
                                    <div className="act-qna-wrapper">
                                        <div className="act-question-qualifier">
                                            <HighlighterACT enabled={highlighterEnabled} onHighlight={this.handleHighlightRight}>
                                                <MathKatex2 mixedContent={preparedQuestionQualifier} />
                                            </HighlighterACT>
                                        </div>
                                        <AnswersGroup
                                            questionNumber={questionNumber}
                                            options={options}
                                            selectedAnswer={userSubmittedAnswer}
                                            eliminatedInfo={eliminatedInfo}
                                            reverseMaskedInfo={reverseMaskedInfo}
                                            onSelect={this.handleAnswer}
                                            onEliminate={this.handleEliminate}
                                            onReverseMask={this.handleReverseMask}
                                        />
                                    </div>
                                </div>
                            </>
                            :
                            <>
                                <div className="act-question-single-panel">
                                    <div className="act-question-qualifier">
                                        <HighlighterACT enabled={highlighterEnabled} onHighlight={this.handleHighlightRight}>
                                            <MathKatex2 mixedContent={preparedQuestionQualifier} />
                                        </HighlighterACT>
                                    </div>
                                    <AnswersGroup
                                        questionNumber={questionNumber}
                                        options={options}
                                        selectedAnswer={userSubmittedAnswer}
                                        eliminatedInfo={eliminatedInfo}
                                        reverseMaskedInfo={reverseMaskedInfo}
                                        onSelect={this.handleAnswer}
                                        onEliminate={this.handleEliminate}
                                        onReverseMask={this.handleReverseMask}
                                    />
                                </div>
                            </>
                        }
                    </div>
                    <div className="act-bottom-navigation">
                         <button className="eact-btn eact-btn--prev" disabled={disableBack} onClick={onBack}>
                            <span aria-hidden="true" className="eact-icon-chevron-left"></span>
                            <span className="show-sm">Prev</span>
                        </button>
                        <button className="eact-btn eact-btn--next" disabled={disableNext} onClick={onNext}>
                            <span className="show-sm">Next</span>
                            <span aria-hidden="true" className="eact-icon-chevron-right"></span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}

// --- Helpers ---

function QuestionInfo(props) {
    let {question, totalQuestionsCount, onBookmark, onAllowCrossOut} = props
    let {subjectName, lessonName, questionNumber } = question


      
    return (
        <div className="act-info-bar">
            <div>
                
                <b>{subjectName}</b> Question {questionNumber}
                {totalQuestionsCount && (
                    <> of {totalQuestionsCount}</>
                )}
            </div>
            <div>
                <Bookmark question={question} onBookmark={onBookmark}/>
            </div>
        </div>
    )
}

function Bookmark(props) {
    let {question, onBookmark} = props
    let {bookmarked} = question
    let imgUrl = `/assets/images/icons/v2/${!bookmarked ? "ico-bookmark-fill-dark.svg" : "ico-bookmark.svg"}`

    return (
        <button className={`eact-btn act-bookmark-btn ${bookmarked ? "bookmarked": ""}`} onClick={onBookmark}>
            <span className="act-bookmark-icon">
                <svg width="14" height="19" viewBox="0 0 15 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M0.87868 0.87868C1.44129 0.316071 2.20435 0 3 0H12C12.7956 0 13.5587 0.31607 14.1213 0.87868C14.6839 1.44129 15 2.20435 15 3V20.25C15 20.5453 14.8267 20.8132 14.5572 20.9342C14.2878 21.0552 13.9725 21.0068 13.7517 20.8106L7.5 15.2535L1.24827 20.8106C1.02753 21.0068 0.712183 21.0552 0.442758 20.9342C0.173333 20.8132 0 20.5453 0 20.25V3C0 2.20435 0.316071 1.44129 0.87868 0.87868Z" fill="currentColor"/>
                </svg>
            </span>
            <span className={`act-bookmark-label`}>FLAG</span>
        </button>
    )
}
